import { Box, Grid, styled } from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ICommunicationLog,
  searchCommunicationCallLogs,
  ICallLogSearch,
  getUserByEmail,
  IUserDTO,
} from 'wisestep-api';
import {
  WsColors,
  WsListingLoader,
  WsTitleBanner,
  WsTypoColors,
  WsTypography,
  activityFormattedDate,
  WsIconNoUserGeneric,
} from 'wisestep-base';

import NoDataFoundCard from '../../components/noDataFoundCard';
import { openUrlInNewTab } from '../../utils/openUrlInNewTab';

import CallLogsSearch from './components';

interface ICallLogs {
  content: ICommunicationLog[];
  empty: boolean;
  first: boolean;
  last: boolean;
  number: number;
  numberOfElements: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

const CallLogs = () => {
  const [smsLogs, setSmsLogs] = useState<ICallLogs | null>(null);
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(100);

  const defaultValues = useRef<ICallLogSearch>({
    startDate: new Date(new Date().setDate(new Date().getDate() - 3)),
    endDate: new Date(),
    serviceId: '',
    query: '',
    type: 'CALL',
    page: 1,
    size: 100,
  });
  const LayoutTheme = styled(Box)(() => ({
    maxWidth: '100%',
    maxHeight: 'xs',
    backgroundColor: WsColors.TertiaryShades50,
    padding: 2,
  }));

  const openDialog = (data: ICommunicationLog) => {
    sessionStorage.setItem('jobsUrls', `/call-logs`);
    data.candidateId &&
      navigate(`/candidate-layout/profile?id=${data.candidateId}&array=${[data.candidateId]}`);
  };
  const searchCallLogs = useCallback(
    async (payload: ICallLogSearch) => {
      const updatedPayload = {
        ...payload,
        page: Math.max(page - 1, 0),
        size: pageSize ?? 100,
      };

      defaultValues.current = updatedPayload;

      try {
        setLoader(true);
        const response = await searchCommunicationCallLogs(updatedPayload);

        const updatedData = response?.data?.content?.map((callLog) => ({
          ...callLog,
          duration: new Date((callLog?.duration ?? 0) * 1000).toISOString().substr(11, 8),
          callStartTime: activityFormattedDate(callLog?.callStartTime),
        }));

        setSmsLogs({
          ...response.data,
          content: updatedData,
        });
      } catch (error) {
        console.error('Error fetching call logs:', error);
        setSmsLogs(null);
      } finally {
        setLoader(false);
      }
    },
    [page, pageSize]
  );

  useEffect(() => {
    searchCallLogs(defaultValues.current);
  }, [searchCallLogs]);

  const getIdByEmail = async (email) => {
    const response = await getUserByEmail(email);
    const userRes: IUserDTO = response.data;
    openUrlInNewTab(
      `/recruiter/profile?id=${String(userRes.id)}&email=${email}&isLink=true&isOverLap=yes`
    );
  };

  const columns: GridColDef[] = [
    { headerName: 'App', field: 'service', flex: 0.5 },
    {
      headerName: 'Recruiter',
      field: 'createdBy',
      flex: 1,
      renderCell: (params) => {
        const callLog = params.row as ICommunicationLog;
        return (
          <WsTypography
            onClick={() => getIdByEmail(callLog.createdBy)}
            color={WsTypoColors.PRIMARY}
            cursor={'pointer'}
          >
            {callLog.createdBy}
          </WsTypography>
        );
      },
    },
    { headerName: 'From', field: 'fromPh', flex: 1 },
    {
      headerName: 'To',
      field: 'toPh',
      flex: 1,
      renderCell: (params) => {
        const callLog = params.row as ICommunicationLog;
        if (callLog.candidateName) {
          return (
            <WsTypography
              onClick={() => openDialog(callLog)}
              color={WsTypoColors.PRIMARY}
              cursor={'pointer'}
            >
              {callLog.candidateName}
            </WsTypography>
          );
        } else {
          return <WsTypography>{callLog.to}</WsTypography>;
        }
      },
    },
    { headerName: 'Direction', field: 'direction', flex: 1 },
    {
      headerName: 'Status',
      field: 'messageStatus',
      flex: 1,
      renderCell: (params) => {
        const status = params.row.messageStatus
          ? `${'Call ' + params.row.messageStatus}`
          : params.row.wisestepStatus;
        return <WsTypography>{status}</WsTypography>;
      },
    },
    { headerName: 'Date', field: 'callStartTime', flex: 1 },
    { headerName: 'Duration', field: 'duration', flex: 0.5 },
  ];

  return (
    <LayoutTheme>
      <WsTitleBanner title="Call Logs" goBack={() => navigate('/')} />
      <Grid item xs={12} bgcolor={WsColors.White} height={120} sx={{ p: 1 }}>
        <CallLogsSearch onSearch={searchCallLogs} defaultValues={defaultValues.current} />
      </Grid>
      <Grid item xs={12} bgcolor={WsColors.White} style={{ height: '66vh', marginBottom: 7 }}>
        {isEmpty(smsLogs?.content) && !loader ? (
          <NoDataFoundCard
            text="No Call logs are recorded"
            icon={<WsIconNoUserGeneric />}
            subElement={<WsTypography>No Calls</WsTypography>}
          />
        ) : loader ? (
          <WsListingLoader />
        ) : (
          <DataGrid
            components={{ Toolbar: GridToolbar }}
            columns={columns}
            rows={smsLogs?.content ?? []}
            page={page - 1}
            rowCount={smsLogs?.totalElements}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage + 1)}
            onPageSizeChange={(newSize) => setPageSize(newSize)}
            initialState={{
              pagination: {
                pageSize: 25,
              },
              sorting: {
                sortModel: [{ field: 'submitted', sort: 'desc' }],
              },
            }}
            rowsPerPageOptions={[25, 50, 100, 250, 500]}
            hideFooterSelectedRowCount
          />
        )}
      </Grid>
    </LayoutTheme>
  );
};

export default CallLogs;
